import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Bike, Gavel, Tag, KeyRound, Coins } from "lucide-react";
import SharingFieldsSection from "../SharingFieldsSection";
import { useVehicleCategories } from "../../../lib/systemConfig";

/**
 * CategorySaleTypeSection — Top of the CreateListingPage form.
 * Combines: Kategorie-Auswahl (Motorrad / Fahrrad) + Verkaufsart (B2B only)
 * + Auctions-Zielgruppe + Sharing-Felder.
 *
 * Pure presentational — receives form state + helpers from the parent.
 */
export default function CategorySaleTypeSection({
    form,
    update,
    isMoto,
    isAuction,
    isDealer,
    auctionsAllowed,
    defaultAuctionEnd,
    user,                // Feb-2026 (User-Wunsch 26 May): zur Auto-Vorbelegung der Kategorie
    afterCategorySlot,   // Mai-2026 (User-Wunsch 21 May Iteration 7): ZB1/COC-Scan unter Kategorie
    afterSaleTypeSlot,   // Mai-2026 (User-Wunsch 21 May Iteration 7): Voice-Recorder unter Verkaufsart
}) {
    const { t } = useTranslation();
    const { motorrad: sysMoto, fahrrad: sysBike } = useVehicleCategories();

    // Feb-2026 (User-Wunsch 26 May): Wenn der Händler in seinem Profil nur eine
    // Fahrzeug-Art markiert hat (Motorrad ODER Fahrrad), zeigen wir auch nur
    // diese Kategorie. Wenn beide markiert sind, bleibt die 2-Spalten-Auswahl
    // wie bisher. Privat-Nutzer (kein Dealer-Profil) sehen ebenfalls beide.
    const dealerHasMoto = user?.dealer_motorcycles !== false; // default an
    const dealerHasBike = !!user?.dealer_bicycles;
    const dealerOnlyMoto = isDealer && dealerHasMoto && !dealerHasBike;
    const dealerOnlyBike = isDealer && !dealerHasMoto && dealerHasBike;
    // Jun-2026 — System-weiter Admin-Schalter: deaktivierte Kategorien werden
    // überall ausgeblendet (Standard: nur Motorrad).
    const showMoto = !dealerOnlyBike && sysMoto;
    const showBike = !dealerOnlyMoto && sysBike;

    // Wenn nur eine Kategorie verfügbar ist, diese automatisch erzwingen.
    useEffect(() => {
        if (showMoto && !showBike && form.category !== "motorcycle") update("category", "motorcycle");
        else if (showBike && !showMoto && form.category !== "bicycle") update("category", "bicycle");
    }, [showMoto, showBike]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {/* Category */}
            <div className="bg-white border border-gray-200 rounded-sm p-6">
                <div className={`grid ${showMoto && showBike ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
                    {showMoto && (
                    <button
                        type="button"
                        onClick={() => update("category", "motorcycle")}
                        className={`p-4 border-2 rounded-sm flex flex-col items-center gap-2 transition-colors ${isMoto ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                        data-testid="cat-select-motorcycle"
                    >
                        <Bike className="w-6 h-6" />
                        <div className="font-bold">{t("buy_request.category_motorcycle")}</div>
                    </button>
                    )}
                    {showBike && (
                    <button
                        type="button"
                        onClick={() => update("category", "bicycle")}
                        className={`p-4 border-2 rounded-sm flex flex-col items-center gap-2 transition-colors ${!isMoto ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                        data-testid="cat-select-bicycle"
                    >
                        <Bike className="w-6 h-6" />
                        <div className="font-bold">{t("buy_request.category_bicycle")}</div>
                    </button>
                    )}
                </div>
            </div>

            {/* Mai-2026 (User-Wunsch 21 May Iteration 7) — ZB1/COC-Scanner direkt unter Kategorie (nur bei Motorrad/Roller). */}
            {afterCategorySlot}

            {/* Sale type (B2B) */}
            {isDealer && (
                <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="sale-type-section">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <button
                            type="button"
                            onClick={() => update("sale_type", "fixed")}
                            className={`p-4 border-2 rounded-sm flex items-start gap-3 text-left transition-colors ${form.sale_type === "fixed" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                            data-testid="sale-type-fixed"
                        >
                            <Tag className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-bold">{t("create_listing.sale_fixed")}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{t("create_listing.sale_fixed_hint")}</div>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                update("sale_type", "rental");
                                update("sharing_enabled", true);
                            }}
                            className={`p-4 border-2 rounded-sm flex items-start gap-3 text-left transition-colors ${form.sale_type === "rental" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                            data-testid="sale-type-rental"
                        >
                            <KeyRound className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-bold">Sharing / Vermietung</div>
                                <div className="text-xs text-gray-500 mt-0.5">Woche, Monat, Saison mieten.</div>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (!auctionsAllowed) return;
                                update("sale_type", "auction");
                                if (!form.auction_end && defaultAuctionEnd) update("auction_end", defaultAuctionEnd());
                                if (!form.auction_audience) update("auction_audience", "b2b_only");
                            }}
                            disabled={!auctionsAllowed}
                            className={`p-4 border-2 rounded-sm flex items-start gap-3 text-left transition-colors ${isAuction ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"} ${!auctionsAllowed ? "opacity-50 cursor-not-allowed hover:border-gray-200" : ""}`}
                            data-testid="sale-type-auction"
                            title={!auctionsAllowed ? "Auktionen sind in Ihren Marktplatzeinstellungen deaktiviert." : ""}
                        >
                            <Gavel className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-bold">Auktion</div>
                                <div className="text-xs text-gray-500 mt-0.5">{t("create_listing.sale_auction_hint")}</div>
                                {!auctionsAllowed && (
                                    <div className="text-[10px] text-amber-700 mt-1 font-bold" data-testid="auctions-disabled-hint">
                                        In Marktplatzeinstellungen deaktiviert
                                    </div>
                                )}
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => update("sale_type", "pawn")}
                            className={`p-4 border-2 rounded-sm flex items-start gap-3 text-left transition-colors ${form.sale_type === "pawn" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                            data-testid="sale-type-pawn"
                        >
                            <Coins className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-bold">Pfand / Refinanzieren</div>
                                <div className="text-xs text-gray-500 mt-0.5">Fahrzeug gegen Auszahlung hinterlegen, später zurückkaufen.</div>
                            </div>
                        </button>
                    </div>

                    {/* Auctions audience */}
                    {isAuction && (
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="auction-audience-row">
                            <button
                                type="button"
                                onClick={() => update("auction_audience", "b2c_b2b")}
                                className={`p-3 border-2 rounded-sm text-left transition-colors ${form.auction_audience === "b2c_b2b" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                                data-testid="auction-audience-b2c-b2b"
                            >
                                <div className="font-bold text-sm">Auktion B2C (privat) + B2B (gewerbe)</div>
                                <div className="text-[11px] text-gray-500 mt-0.5">Private Endkunden &amp; Händler dürfen bieten.</div>
                            </button>
                            <button
                                type="button"
                                onClick={() => update("auction_audience", "b2b_only")}
                                className={`p-3 border-2 rounded-sm text-left transition-colors ${(!form.auction_audience || form.auction_audience === "b2b_only") ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                                data-testid="auction-audience-b2b-only"
                            >
                                <div className="font-bold text-sm">Auktion nur B2B (gewerbe)</div>
                                <div className="text-[11px] text-gray-500 mt-0.5">Nur verifizierte Händler dürfen bieten.</div>
                            </button>
                        </div>
                    )}

                    {/* Sharing inline */}
                    {form.sale_type === "rental" && (
                        <div className="mt-4" data-testid="sale-type-rental-fields">
                            <SharingFieldsSection form={form} update={update} visible hideHeader />
                        </div>
                    )}
                </div>
            )}

            {/* Sale type (B2C) — Privatkunden können zwischen Festpreis-Verkauf
                und Pfand / Refinanzieren wählen. Auktion/Vermietung bleiben B2B. */}
            {!isDealer && (
                <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="sale-type-section-b2c">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => update("sale_type", "fixed")}
                            className={`p-4 border-2 rounded-sm flex items-start gap-3 text-left transition-colors ${form.sale_type !== "pawn" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                            data-testid="sale-type-fixed-b2c"
                        >
                            <Tag className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-bold">{t("create_listing.sale_fixed")}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{t("create_listing.sale_fixed_hint")}</div>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => update("sale_type", "pawn")}
                            className={`p-4 border-2 rounded-sm flex items-start gap-3 text-left transition-colors ${form.sale_type === "pawn" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                            data-testid="sale-type-pawn-b2c"
                        >
                            <Coins className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-bold">Pfand / Refinanzieren</div>
                                <div className="text-xs text-gray-500 mt-0.5">Fahrzeug gegen Auszahlung hinterlegen, später zurückkaufen.</div>
                            </div>
                        </button>
                    </div>
                </div>
            )}

            {/* Mai-2026 (User-Wunsch 21 May Iteration 7) — KI-Sprachaufnahme unter Verkaufsart. */}
            {afterSaleTypeSlot}
        </>
    );
}
